import { Injectable } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { Notify } from 'notiflix';
import { BehaviorSubject, interval } from 'rxjs';
import { ContactsService } from 'src/app/modules/widgets/contacts/contacts.service';
import { AgoraService } from './agora.service';
import { AuthService } from './auth.service';
import { IncomingCallService } from './incoming-call.service';
import { OutgoingCallService } from './outgoing-call.service';
import { RoutingService } from './routing.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private interval: any = null;
  public user: any = null;
  public messageFromSocket$: BehaviorSubject<any>;
  public userTyping$: BehaviorSubject<any>;

  constructor(
    private _socket: Socket,
    private _ics: IncomingCallService,
    private _ocs: OutgoingCallService,
    private _agora: AgoraService,
    private _rs: RoutingService,
    private _contactService: ContactsService,
  ) {
    this.messageFromSocket$ = new BehaviorSubject(null);
    this.userTyping$ = new BehaviorSubject(null);
    this.onIncomingCallHandler();
    this.onOutgoingCallRejectEventHandler();
    this.onIncomingCallRejectEventHandler();
    this.onIncomingCallAcceptEventHandler();
    this.onChatMessageHandler();
    this.onTypingHandler();
    this.onCallEndedHandler();
  }

  public setCurrentUser(user: any) {
    this.user = user;
    const country = user?.country || user?.address?.country || null;
    this._ocs.setLocalUserCountry(country);
  }

  public ngOnDestroy() {
    this.interval = null;
  }

  public emitAuth(token: string | null) {
    this._socket.emit('authenticate', { token: token }, (res: any) => {
      console.log('authenticate=>', res);
      if (res) {
        if (res.statusCode != 200) {
          Notify.warning(
            'Failed to connect to user network, Please refresh your browser & try again',
          );
        }

        if (res.statusCode == 200) {
          if (this.user) {
            this.user.socketId = this._socket.id;
            this.user.status = 'online';
          }
          this.interval = interval(5000).subscribe(() => {
            this.emitGetOnlineUserEvent();
          });
        }
      } else {
        Notify.warning(
          'Failed to connect to futuristiclevelz network, Please refresh your browser & try again',
        );
      }
    });
  }

  public initiateCall(userId: string, isVideoEnabled: boolean) {
    this._socket.emit(
      'initiateCall',
      { toUser: userId, isVideoEnabled: isVideoEnabled },
      (res: any) => {
        if (res.handlerCode == 'NOT_CONNECTED_TO_NETWORK') {
          console.error(res.handlerCode);
          Notify.warning(
            'You are not connected to network, Please reload the page!',
          );
        } else if (res.handlerCode == 'CALLEE_NOT_CONNECTED_TO_NETWORK') {
          console.error(res.handlerCode);
          Notify.warning('User is not avaliable for call!');
        } else if (res.handlerCode == 'CALLEE_NOT_INFO_MISSING') {
          console.error(res.handlerCode);
          Notify.warning('User is not avaliable for call!');
        } else {
          console.log(res.data);
          this._ocs.outgoingCallSubject.next(res.data);
          const calleeId = res.data?.receiverId;
          const enrichedCallee = this._contactService.contacts?.find((c: any) =>
            String(c._id || c.userId) === String(calleeId)
          ) || { _id: calleeId, name: 'User' };
          console.log('SocketService: setRinging → this.user:', JSON.stringify(this.user));
          console.log('SocketService: setRinging → enrichedCallee:', JSON.stringify(enrichedCallee));
          this._ocs.setRinging(this.user, enrichedCallee, isVideoEnabled);
        }
      },
    );
  }

  public emitOutgoingCallRejectEvent(calleeId: string) {
    this._socket.emit(
      'outgoingCallRejectEvent',
      { calleeId: calleeId },
      (res: any) => {
        this._ocs.clearCallState('outgoing_reject');
        console.log(res);
      },
    );
  }

  public emitIncomingCallRejectEvent(callerId: string) {
    this._socket.emit(
      'incomingCallRejectEvent',
      { callerId: callerId },
      (res: any) => {
        this._ocs.clearCallState('incoming_reject');
        console.log(res);
      },
    );
  }

  /**
   * CALLEE accepts the call.
   * The server callback (res) contains the CALLEE's own rtcToken + rtcUid.
   * Backend log: "Callee Token: ✅ Generated (UID: 1739575036)"
   */
  public emitIncomingCallAcceptEvent(caller: any, isVideoEnabled: boolean) {
    console.log('SocketService: emitIncomingCallAcceptEvent initiating...', {
      callerId: caller?._id,
      isVideoEnabled,
    });

    this._socket.emit(
      'incomingCallAcceptEvent',
      { caller: caller?._id || caller, isVideoEnabled },
      (res: any) => {
        console.log('CALLEE ← incomingCallAcceptEvent callback RAW:', res);

        // The callback response is the CALLEE's token data
        const resData = res?.data || res;
        const channelId = resData?.channelId || null;
        const rtcToken = resData?.rtcToken || null;
        const rtcUid = resData?.rtcUid || null;

        console.log('CALLEE token data:', {
          channelId,
          rtcToken: rtcToken ? rtcToken.slice(0, 12) + '...' : 'MISSING',
          rtcUid,
        });

        if (!channelId) {
          Notify.failure(
            'Failed to join call: Channel ID missing from server response.',
          );
          console.error('CALLEE: Missing channelId in accept callback:', res);
          return;
        }

        if (!rtcToken || !rtcUid) {
          Notify.failure(
            'Failed to join call: Token or UID missing from server response.',
          );
          console.error(
            'CALLEE: Missing rtcToken or rtcUid in accept callback:',
            res,
          );
          return;
        }

        const enrichedCaller = this._contactService.contacts?.find((c: any) =>
          String(c._id || c.userId) === String(caller?._id || caller)
        ) || caller;
        this._ocs.setActive(this.user, enrichedCaller, channelId, !!isVideoEnabled);
        this._agora.initAndStartCall(!!isVideoEnabled, caller, channelId, {
          rtcToken,
          rtcUid,
        });
      },
    );
  }

  public emitGetOnlineUserEvent() {
    this._socket.emit('emitGetOnlineUserEvent', {}, (res: any) => {
      if (res?.message?.data) {
        this._contactService.updateActiveStatus(res.message.data);
      }
    });
  }

  public onIncomingCallHandler() {
    this._socket.on('incomingCallHandler', (data: any) => {
      console.log('SocketService: incomingCallHandler received', data);
      this._rs.home();
      this._ics.incomingCallSubject.next(data);
    });
  }

  public onOutgoingCallRejectEventHandler() {
    this._socket.on('outgoingCallRejectEventHandler', (data: any) => {
      this._ocs.clearCallState('outgoing_reject_remote');
      this._ics.outgoingCallRejectEventSubject.next(data);
    });
  }

  public onIncomingCallRejectEventHandler() {
    this._socket.on('incomingCallRejectEventHandler', (data: any) => {
      this._ocs.clearCallState('incoming_reject_remote');
      this._ocs.incomingCallRejectEventSubject.next(data);
    });
  }

  /**
   * CALLER receives notification that callee accepted.
   * The server pushes the CALLER's own rtcToken + rtcUid in this event.
   * Backend log: "Caller Token: ✅ Generated (UID: 289007882)"
   *
   * CRITICAL: The server MUST send callerRtcToken + callerRtcUid (not the callee's).
   * Field names below must match exactly what your backend emits to the caller's socket.
   */
  public onIncomingCallAcceptEventHandler() {
    this._socket.on('incomingCallAcceptEventHandler', (data: any) => {
      console.log('CALLER ← incomingCallAcceptEventHandler RAW:', data);

      // ── Safely extract CALLER's token ──────────────────────────────────────
      // Adjust field names if your backend uses different keys.
      // Common patterns: callerRtcToken / rtcToken / data.rtcToken
      const channelId = data?.channelId || data?.data?.channelId || null;

      // Try caller-specific fields first, then fall back to generic fields
      const rtcToken =
        data?.callerRtcToken || // preferred: explicitly named caller token
        data?.rtcToken || // generic fallback
        data?.data?.rtcToken || // nested fallback
        null;

      const rtcUid =
        data?.callerRtcUid || // preferred: explicitly named caller uid
        data?.rtcUid || // generic fallback
        data?.data?.rtcUid || // nested fallback
        null;

      console.log('CALLER token data:', {
        channelId,
        rtcToken: rtcToken ? rtcToken.slice(0, 12) + '...' : 'MISSING',
        rtcUid,
      });

      if (!channelId) {
        Notify.failure('Call setup failed: Channel ID missing.');
        console.error(
          'CALLER: Missing channelId in incomingCallAcceptEventHandler:',
          data,
        );
        return;
      }

      if (!rtcToken || !rtcUid) {
        Notify.failure('Call setup failed: Caller token or UID missing.');
        console.error(
          'CALLER: Missing rtcToken or rtcUid in incomingCallAcceptEventHandler:',
          data,
        );
        console.error(
          'CALLER: Your backend must send callerRtcToken + callerRtcUid in this event.',
        );
        return;
      }

      const myId = String(this.user?._id || this.user?.id || '');
      const calleeId = String(data?.callee?._id || data?.callee?.id || '');
      const callerId = String(
        data?.caller?._id || data?.caller?.id || data?.caller || '',
      );

      const remoteUser =
        calleeId && calleeId !== myId
          ? data.callee
          : callerId && callerId !== myId
            ? data.caller
            : null;

      const enrichedRemote = this._contactService.contacts?.find((c: any) =>
        String(c._id || c.userId) === String(remoteUser?._id || remoteUser?.id)
      ) || remoteUser;
      this._ocs.setActive(
        this.user,
        enrichedRemote,
        channelId,
        !!data?.isVideoEnabled,
      );
      this._agora.initAndStartCall(
        !!data.isVideoEnabled,
        remoteUser,
        channelId,
        { rtcToken, rtcUid },
      );
      this._ocs.incomingCallAcceptEventSubject.next(data);
    });
  }

  public emitLogoutEvent(userId: any) {
    this._socket.emit('logout', { userId: userId }, (res: any) => { });
  }

  public emitChatMessage(toUserId: string, message: string, type: 'text' | 'voice' = 'text') {
    this._socket.emit('chatMessage', { toUserId, message, type }, (res: any) => {
      if (!res || res.statusCode !== 200) {
        Notify.failure('Fallback message delivery failed');
      }
    });
  }

  public emitTyping(toUserId: string, isTyping: boolean) {
    this._socket.emit('typing', { toUserId, isTyping });
  }

  public onTypingHandler() {
    this._socket.on('userTyping', (data: any) => {
      this.userTyping$.next(data);
    });
  }

  public emitLeaveCall(channelId: string, userId: string) {
    this._ocs.clearCallState('leave_call');
    this._socket.emit('leaveCall', { channelId, userId }, () => { });
  }

  private onChatMessageHandler() {
    this._socket.on('incomingMessage', (data: any) => {
      console.log('Socket Message Received:', data);
      if (data) {
        const standardizedData = {
          ...data,
          fromUserId: data.fromUserId || data.from || data.userId,
          message: data.message || data.text,
        };
        console.log('Standardized Socket Data:', standardizedData);
        if (standardizedData.fromUserId !== this.user?._id) {
          this.playMessageSound();
        }
        this.messageFromSocket$.next(standardizedData);
      }
    });
  }

  private playMessageSound() {
    try {
      const audio = new Audio();
      audio.src = environment.webUrl + 'assets/audio/incoming.wav';
      audio.load();
      audio
        .play()
        .catch((err) =>
          console.log('Message sound play deferred/failed:', err),
        );
    } catch (e) {
      console.log('Sound notification error:', e);
    }
  }

  private onCallEndedHandler() {
    this._socket.on('callEnded', () => {
      this._ocs.clearCallState('call_ended');
      this._agora.stop();
    });
  }
}
